import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';

interface HeroBannerCache {
  subject: BehaviorSubject<any>;
  observable?: Observable<any>;
  fetchedAt: number;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  constructor(private readonly http: HttpClient) {}

  getActiveTopBarAnnouncements(language: 'en' | 'fr' | 'pl'): Observable<any[]> {
    return this.http.get<any[]>(
      `${environment.baseUrl}/settings__top-bar-announcements/active`,
      { params: { language } },
    );
  }

  // Hero banners SWR cache
  private readonly heroBannersCaches = new Map<string, HeroBannerCache>();
  private readonly HERO_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private readonly HERO_LOCAL_KEY_PREFIX = 'hero_banners_cache_v2_';
  private readonly HERO_LOCAL_TS_KEY_PREFIX = 'hero_banners_cache_ts_v2_';

  /* USERS */
  createUser(payload: any) {
    const finalRoute = `${environment.baseUrl}/settings__user`;
    return this.http.post(finalRoute, payload);
  }

  /* SLIDES */

  getSlides(
    page: number = 1,
    limit: number = 5,
    query: any = {}
  ): Observable<any> {
    const finalRoute = `${environment.baseUrl}/settings__slider/paginate?page=${page}&limit=${limit}&show=true`;
    return this.http.post<any>(finalRoute, query);
  }

  /* HERO BANNERS */
  getHeroBanners(page: number = 1, limit: number = 10, query: any = {}): Observable<any> {
    const finalRoute = `${environment.baseUrl}/settings__hero-banner/paginate?page=${page}&limit=${limit}`;
    const cacheLanguage = query.language === 'pl' || query.language === 'en' ? query.language : 'all';
    let cache = this.heroBannersCaches.get(cacheLanguage);

    if (!cache) {
      cache = { subject: new BehaviorSubject<any>({ data: [] }), fetchedAt: 0 };
      const localJson = this.safeLocalStorageGet(`${this.HERO_LOCAL_KEY_PREFIX}${cacheLanguage}`);
      const localTsStr = this.safeLocalStorageGet(`${this.HERO_LOCAL_TS_KEY_PREFIX}${cacheLanguage}`);
      const localTs = localTsStr ? Number(localTsStr) : 0;

      if (localJson && localTs && Date.now() - localTs < this.HERO_CACHE_DURATION) {
        try {
          const parsed = JSON.parse(localJson);
          if (parsed && parsed.data) {
            cache.subject.next(parsed);
            cache.fetchedAt = localTs;
          }
        } catch {}
      }
      this.heroBannersCaches.set(cacheLanguage, cache);
    }

    if (cache.observable && Date.now() - cache.fetchedAt < this.HERO_CACHE_DURATION) {
      return cache.subject.asObservable();
    }

    const currentCache = cache;
    currentCache.observable = this.http.post<any>(finalRoute, query).pipe(
      tap((res) => {
        currentCache.fetchedAt = Date.now();
        this.safeLocalStorageSet(
          `${this.HERO_LOCAL_KEY_PREFIX}${cacheLanguage}`,
          JSON.stringify(res),
        );
        this.safeLocalStorageSet(
          `${this.HERO_LOCAL_TS_KEY_PREFIX}${cacheLanguage}`,
          String(currentCache.fetchedAt),
        );
      }),
      catchError((err) => {
        this.invalidateHeroCache(cacheLanguage);
        return of({ data: [] });
      }),
      shareReplay(1)
    );

    currentCache.observable.subscribe({
      next: (res) => currentCache.subject.next(res),
    });
    return currentCache.subject.asObservable();
  }

  invalidateHeroCache(language?: string): void {
    const cacheLanguages = language ? [language] : ['all', 'en', 'pl'];
    for (const cacheLanguage of cacheLanguages) {
      this.heroBannersCaches.delete(cacheLanguage);
      this.safeLocalStorageRemove(`${this.HERO_LOCAL_KEY_PREFIX}${cacheLanguage}`);
      this.safeLocalStorageRemove(`${this.HERO_LOCAL_TS_KEY_PREFIX}${cacheLanguage}`);
    }
  }

  private safeLocalStorageGet(key: string): string | null {
    try { return localStorage.getItem(key); } catch { return null; }
  }
  private safeLocalStorageSet(key: string, value: string): void {
    try { localStorage.setItem(key, value); } catch { /* ignore */ }
  }
  private safeLocalStorageRemove(key: string): void {
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
}
